import { useEffect, useMemo, useState } from "react";
import {
  Building2,
  ClipboardList,
  Clock3,
  Mail,
  MessageSquarePlus,
  Phone,
  X
} from "lucide-react";
import {
  addManagementTaskNote,
  subscribeDemoRequestTasks,
  updateManagementTask
} from "../services/managementTaskService";

const columns = [
  { id: "new", label: "Yeni Talep", color: "#0ea5e9", tint: "#effaff" },
  { id: "contacted", label: "İletişime Geçildi", color: "#8b5cf6", tint: "#f7f3ff" },
  { id: "scheduled", label: "Demo Planlandı", color: "#f59e0b", tint: "#fff9ed" },
  { id: "proposal", label: "Teklif / Değerlendirme", color: "#f97316", tint: "#fff5ed" },
  { id: "completed", label: "Tamamlandı", color: "#16a34a", tint: "#effbf3" }
];

const priorityLabels = {
  low: "Düşük",
  normal: "Normal",
  high: "Yüksek",
  urgent: "Acil"
};

const toDateInput = (value) => {
  const date = value?.toDate?.() || (value instanceof Date ? value : null);
  if (!date) return "";
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
};

const formatDate = (value, withTime = false) => {
  const date = value?.toDate?.() || (value instanceof Date ? value : null);
  if (!date) return "—";
  return new Intl.DateTimeFormat("tr-TR", withTime
    ? { dateStyle: "medium", timeStyle: "short" }
    : { dateStyle: "medium" }).format(date);
};

function DemoRequestKanbanView({
  user,
  users = [],
  themeMode = "light",
  initialTaskId = "",
  onInitialTaskHandled
}) {
  const [tasks, setTasks] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [draggedId, setDraggedId] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const dark = themeMode === "dark";
  const colors = {
    text: dark ? "#e5edf6" : "#123f4c",
    muted: dark ? "#9fb0c4" : "#64748b",
    surface: dark ? "#172235" : "#fff",
    border: dark ? "#334155" : "#dbe5ea",
    input: dark ? "#101b2c" : "#f8fafc"
  };

  useEffect(() => subscribeDemoRequestTasks(
    setTasks,
    (reason) => setError(reason.message || "Demo talepleri alınamadı.")
  ), []);

  useEffect(() => {
    if (!initialTaskId || !tasks.some((task) => task.id === initialTaskId)) return;
    setSelectedId(initialTaskId);
    onInitialTaskHandled?.();
  }, [initialTaskId, onInitialTaskHandled, tasks]);

  const selected = useMemo(
    () => tasks.find((task) => task.id === selectedId) || null,
    [selectedId, tasks]
  );
  const activeUsers = users.filter((item) => item.isActive !== false);

  const changeTask = async (taskId, changes) => {
    setSaving(true);
    setError("");
    try {
      await updateManagementTask(taskId, changes);
    } catch (reason) {
      setError(reason.message || "Görev güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  const assignTask = async (task, uid) => {
    const assignee = activeUsers.find((item) => item.uid === uid || item.id === uid);
    await changeTask(task.id, {
      assigneeId: uid,
      assigneeName: assignee?.name || assignee?.displayName || assignee?.email || ""
    });
  };

  const saveNote = async () => {
    if (!selected || !note.trim()) return;
    setSaving(true);
    setError("");
    try {
      await addManagementTaskNote({ taskId: selected.id, user, note });
      setNote("");
    } catch (reason) {
      setError(reason.message || "Not eklenemedi.");
    } finally {
      setSaving(false);
    }
  };

  return <div style={{ color: colors.text }}>
    <div style={{ display: "flex", justifyContent: "space-between", gap: 20, alignItems: "flex-start", marginBottom: 22 }}>
      <div>
        <h2 style={{ margin: 0, display: "flex", alignItems: "center", gap: 9 }}><ClipboardList size={23}/> Demo Talepleri</h2>
        <p style={{ margin: "7px 0 0", color: colors.muted, fontWeight: 700 }}>Web sitesinden gelen demo taleplerinin görüşme ve teklif süreci</p>
      </div>
      <div style={{ padding: "9px 13px", borderRadius: 999, background: dark ? "#123f38" : "#ecfdf5", color: dark ? "#a7f3d0" : "#047857", fontWeight: 900, fontSize: 12 }}>{tasks.filter((task) => task.status !== "completed").length} aktif görev</div>
    </div>

    {error && <div style={{ padding: 12, marginBottom: 15, borderRadius: 11, background: dark ? "#461c24" : "#fef2f2", color: dark ? "#fecaca" : "#b91c1c", fontWeight: 750 }}>{error}</div>}

    <div style={{ display: "grid", gridTemplateColumns: "repeat(5,minmax(230px,1fr))", gap: 13, overflowX: "auto", paddingBottom: 18 }}>
      {columns.map((column) => {
        const columnTasks = tasks.filter((task) => (task.status || "new") === column.id);
        return <section
          key={column.id}
          onDragOver={(event) => event.preventDefault()}
          onDrop={() => {
            if (draggedId) changeTask(draggedId, { status: column.id });
            setDraggedId("");
          }}
          style={{ minHeight: 520, padding: 10, border: `1px solid ${colors.border}`, borderRadius: 16, background: dark ? "#121d2e" : column.tint }}
        >
          <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 4px 13px", borderBottom: `3px solid ${column.color}` }}>
            <strong style={{ fontSize: 13 }}>{column.label}</strong>
            <span style={{ minWidth: 24, height: 24, display: "grid", placeItems: "center", borderRadius: 999, background: column.color, color: "#fff", fontSize: 11, fontWeight: 950 }}>{columnTasks.length}</span>
          </header>
          <div style={{ display: "grid", gap: 9, marginTop: 11 }}>
            {columnTasks.map((task) => <button
              key={task.id}
              draggable
              onDragStart={() => setDraggedId(task.id)}
              onClick={() => setSelectedId(task.id)}
              style={{ padding: 13, border: `1px solid ${colors.border}`, borderLeft: `4px solid ${column.color}`, borderRadius: 13, background: colors.surface, color: colors.text, textAlign: "left", cursor: "grab", boxShadow: dark ? "none" : "0 8px 22px rgba(15,23,42,.06)" }}
            >
              <strong style={{ display: "block", fontSize: 13, lineHeight: 1.35 }}>{task.companyName}</strong>
              <span style={{ display: "block", marginTop: 5, color: colors.muted, fontSize: 11 }}>{task.contactName}</span>
              <span style={{ display: "block", marginTop: 9, padding: "5px 7px", borderRadius: 8, background: dark ? "#20334a" : "#f1f5f9", fontSize: 10, fontWeight: 800 }}>{task.interest || "Tüm platform"}</span>
              <span style={{ display: "flex", justifyContent: "space-between", gap: 8, marginTop: 10, color: colors.muted, fontSize: 10 }}><span>{task.assigneeName || "Atanmamış"}</span><span>{formatDate(task.createdAt)}</span></span>
            </button>)}
            {!columnTasks.length && <div style={{ padding: "26px 8px", color: colors.muted, textAlign: "center", fontSize: 11 }}>Bu aşamada görev yok.</div>}
          </div>
        </section>;
      })}
    </div>

    {selected && <div onMouseDown={(event) => event.target === event.currentTarget && setSelectedId("")} style={{ position: "fixed", inset: 0, zIndex: 1500, display: "grid", placeItems: "center", padding: 24, background: "rgba(15,23,42,.52)" }}>
      <div style={{ width: "min(760px,96vw)", maxHeight: "90vh", overflowY: "auto", padding: 22, borderRadius: 20, background: colors.surface, border: `1px solid ${colors.border}`, boxShadow: "0 28px 80px rgba(15,23,42,.32)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 15 }}>
          <div><small style={{ color: "#0f766e", fontWeight: 950 }}>DEMO GÖREVİ</small><h3 style={{ margin: "5px 0 0", fontSize: 22 }}>{selected.companyName}</h3><p style={{ margin: "5px 0 0", color: colors.muted }}>{selected.contactName} · {selected.interest}</p></div>
          <button onClick={() => setSelectedId("")} aria-label="Kapat" style={{ width: 36, height: 36, display: "grid", placeItems: "center", border: `1px solid ${colors.border}`, borderRadius: 10, background: "transparent", color: colors.text, cursor: "pointer" }}><X size={18}/></button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 10, marginTop: 20 }}>
          <a href={`mailto:${selected.contactEmail}`} style={infoCard(colors)}><Mail size={17}/><span><small>E-posta</small><strong>{selected.contactEmail}</strong></span></a>
          <a href={selected.contactPhone ? `tel:${selected.contactPhone}` : undefined} style={infoCard(colors)}><Phone size={17}/><span><small>Telefon</small><strong>{selected.contactPhone || "Belirtilmedi"}</strong></span></a>
          <div style={infoCard(colors)}><Building2 size={17}/><span><small>Firma</small><strong>{selected.companyName}</strong></span></div>
          <div style={infoCard(colors)}><Clock3 size={17}/><span><small>Talep tarihi</small><strong>{formatDate(selected.createdAt, true)}</strong></span></div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 12, marginTop: 18 }}>
          <label style={labelStyle(colors)}>Aşama<select value={selected.status || "new"} disabled={saving} onChange={(event) => changeTask(selected.id, { status: event.target.value })} style={inputStyle(colors)}>{columns.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</select></label>
          <label style={labelStyle(colors)}>Sorumlu<select value={selected.assigneeId || ""} disabled={saving} onChange={(event) => assignTask(selected, event.target.value)} style={inputStyle(colors)}><option value="">Atanmamış</option>{activeUsers.map((item) => <option key={item.uid || item.id} value={item.uid || item.id}>{item.name || item.displayName || item.email}</option>)}</select></label>
          <label style={labelStyle(colors)}>Öncelik<select value={selected.priority || "normal"} disabled={saving} onChange={(event) => changeTask(selected.id, { priority: event.target.value })} style={inputStyle(colors)}>{Object.entries(priorityLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label style={labelStyle(colors)}>Hedef tarih<input type="date" value={toDateInput(selected.dueAt)} disabled={saving} onChange={(event) => changeTask(selected.id, { dueAt: event.target.value ? new Date(`${event.target.value}T18:00:00`) : null })} style={inputStyle(colors)}/></label>
        </div>

        <div style={{ marginTop: 20, paddingTop: 18, borderTop: `1px solid ${colors.border}` }}>
          <strong style={{ display: "flex", alignItems: "center", gap: 7 }}><MessageSquarePlus size={18}/> Görüşme notları</strong>
          <div style={{ display: "grid", gap: 8, marginTop: 11 }}>
            {(selected.notes || []).slice().reverse().map((item, index) => <div key={`${item.createdByUid}-${index}`} style={{ padding: 11, borderRadius: 11, background: colors.input }}><div style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.5 }}>{item.body}</div><small style={{ display: "block", marginTop: 6, color: colors.muted }}>{item.createdByName} · {formatDate(item.createdAt, true)}</small></div>)}
            {!selected.notes?.length && <div style={{ color: colors.muted, fontSize: 12 }}>Henüz görüşme notu eklenmedi.</div>}
          </div>
          <textarea value={note} onChange={(event) => setNote(event.target.value)} placeholder="Görüşme sonucu veya sonraki adımı yazın…" style={{ ...inputStyle(colors), minHeight: 92, padding: 12, resize: "vertical", marginTop: 11 }}/>
          <button onClick={saveNote} disabled={saving || !note.trim()} style={{ marginTop: 9, border: 0, borderRadius: 10, padding: "10px 15px", background: "#0f766e", color: "#fff", fontWeight: 900, cursor: "pointer" }}>Notu ekle</button>
        </div>
      </div>
    </div>}
  </div>;
}

const infoCard = (colors) => ({ display: "flex", alignItems: "center", gap: 10, padding: 12, borderRadius: 12, background: colors.input, color: colors.text, textDecoration: "none" });
const labelStyle = (colors) => ({ display: "grid", gap: 6, color: colors.muted, fontSize: 11, fontWeight: 850 });
const inputStyle = (colors) => ({ width: "100%", minHeight: 42, padding: "0 10px", border: `1px solid ${colors.border}`, borderRadius: 10, background: colors.input, color: colors.text, outline: "none" });

export default DemoRequestKanbanView;
